import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { exchangeApi, apiErrorMessage, type Me } from "./api";

// ===========================================================================
// Estado de «mi compañía en el mercado» (ficha, expediente, nivel y flotilla).
// Una sola lectura por layout; las pantallas llaman `reload()` tras un cambio.
// ===========================================================================

interface MeState {
  me: Me | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

const MeContext = createContext<MeState | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      setMe(await exchangeApi.me());
    } catch (err) {
      setError(apiErrorMessage(err, "No se pudo cargar tu información de Cargo Market"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return <MeContext.Provider value={{ me, loading, error, reload }}>{children}</MeContext.Provider>;
}

export function useMe(): MeState {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe debe usarse dentro de <MeProvider>");
  return ctx;
}

/** Carga asíncrona simple con recarga (listas de las pantallas). */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      setData(await fn());
    } catch (err) {
      setError(apiErrorMessage(err, "No se pudo cargar la información"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    void run();
  }, [run]);
  return { data, loading, error, reload: run, setData };
}
